// Nomad-mon Go! 2 — DECK RULES for THE FORGE (the V2 deck builder).
//
// Pure functions only: no DOM, no THREE, no audio, no Math.random. The forge
// (src/deckforge.js) paints what these return. Nothing here changes how a battle
// resolves: the synergies shown are the ENGINE's own (detectSynergies from
// depth.js), the arena shown is the engine's own (arenaFor(lead.type)), and the
// star budget is a deck-LEGALITY rule that lives in the client. See
// ~/dev/nomadmon-worklog/v3/COLLECTION_DECK_V2.md for the server/MP side of it.

import { detectSynergies, longestTypeArc, arenaFor, TYPE_ORDER } from './depth.js';
import { effectivePwr } from './levelling.js';
import { heatWeight, mulberry32, seed32 } from './draw.js';
import { SURGE_PER_ENERGY, SURGE_START_PER_CHARGE } from './sigil/rules.js';

export const FORGE_CHARS = 5;
export const FORGE_SUPPORTS = 2;

// ---------------------------------------------------------------- STAR BUDGET
// Every fighter costs stars by rarity; a squad of five may spend STAR_BUDGET.
// 15 = five epics, or two legendaries + an epic + two rares, or one mythic +
// a legendary + an epic + a rare + a common. Level does NOT raise the cost:
// a levelled common is the cheapest power in the game, which is what makes
// duplicates and the long tail of the binder worth having.
export const STAR_COST = { common: 1, rare: 2, epic: 3, legendary: 4, mythic: 5, eternal: 5 };
export const STAR_BUDGET = 15;
// v11: forged cards (Duos / Fusions) carry their own cost (5-6★, data/fusions.json).
export function starCost(d) { return (d && d.stars) || STAR_COST[(d && d.rarity) || 'common'] || 1; }
export function deckStars(ids, byId) {
  let n = 0;
  for (const id of ids) if (byId[id]) n += starCost(byId[id]);
  return n;
}

// ---------------------------------------------------------------- type maths
const BEATS = {};
for (let i = 0; i < 6; i++) BEATS[TYPE_ORDER[i]] = TYPE_ORDER[(i + 1) % 6];
const WEAK_TO = {};
for (let i = 0; i < 6; i++) WEAK_TO[TYPE_ORDER[i]] = TYPE_ORDER[(i + 5) % 6];

// ---------------------------------------------------------------- BONDS
// The live read-out. `active` is exactly what the engine will apply (same
// function, same cap). `near` names what ONE more card would light, and
// `gainFor(card)` scores a candidate so the tray can badge it.
export function bondsFor(ids, byId, mastery = null) {
  const cards = ids.map(id => byId[id]).filter(Boolean);
  const lookup = {};
  for (const c of cards) lookup[c.id] = c;
  const syn = detectSynergies(cards.map(c => c.id), lookup, mastery);
  const active = syn.list.map(s => ({ key: s.key, label: s.label, blurb: s.blurb,
    detail: s.detail, effect: effectText(s.bonus) }));
  const keys = new Set(active.map(a => a.key));
  const near = [];
  if (cards.length < FORGE_CHARS) {
    const arc = longestTypeArc(cards.map(c => c.type));
    if (!keys.has('arc3') && !keys.has('arc4') && arc.len === 2) {
      const ends = arcEnds(arc.arc);
      near.push({ key: 'arc3', label: 'TYPE ARC', need: ends, text: `ADD ${ends.join(' or ')} → +${SURGE_PER_ENERGY} SURGE/RD` });
    } else if (keys.has('arc3') && arc.len === 3) {
      const ends = arcEnds(arc.arc);
      near.push({ key: 'arc4', label: 'GRAND ARC', need: ends, text: `ADD ${ends.join(' or ')} → GRAND ARC` });
    }
    if (!keys.has('person')) {
      const byP = {};
      for (const c of cards) if (c.person) byP[c.person] = (byP[c.person] || 0) + 1;
      const p = Object.entries(byP).sort((a, b) => b[1] - a[1]).find(([, n]) => n === 2);
      if (p) near.push({ key: 'person', label: `${p[0].toUpperCase()} CYCLE`, person: p[0], text: `1 MORE ${p[0].toUpperCase()} → +5% DMG` });
    }
  }
  return { active, near, bonus: syn.bonus, capped: !!syn.capped };
}
function arcEnds(arc) {
  const i0 = TYPE_ORDER.indexOf(arc[0]);
  const i1 = TYPE_ORDER.indexOf(arc[arc.length - 1]);
  const out = [TYPE_ORDER[(i0 + 5) % 6], TYPE_ORDER[(i1 + 1) % 6]];
  return [...new Set(out.filter(t => !arc.includes(t)))];
}
export function effectText(b) {
  const bits = [];
  if (b && b.energyPerRound) bits.push(`+${b.energyPerRound * SURGE_PER_ENERGY} SURGE/RD`);
  if (b && b.dmgPct) bits.push(`+${b.dmgPct}% DMG`);
  const start = ((b && b.chargeFaster) || 0) + ((b && b.surgeStart) || 0);
  if (start) bits.push(`+${start * SURGE_START_PER_CHARGE} SURGE AT START`);
  if (b && b.neutralizeFirstWeakness) bits.push('FIRST WEAKNESS HIT LANDS NEUTRAL');
  return bits.join(' · ');
}

// What adding `card` to `ids` would newly light (for tray badges). Returns the
// short tag of the strongest new bond, or ''.
export function bondGain(ids, card, byId) {
  if (ids.includes(card.id) || ids.length >= FORGE_CHARS) return '';
  const before = new Set(bondsFor(ids, byId).active.map(a => a.key));
  const after = bondsFor([...ids, card.id], byId).active.map(a => a.key).filter(k => !before.has(k));
  if (!after.length) return '';
  const tag = { arc3: 'ARC', arc4: 'GRAND ARC', person: 'CREW', labborn: 'LAB', labborn3: 'LAB', underdog: 'UNDERDOG' };
  return tag[after[after.length - 1]] || after[0].toUpperCase();
}

// ---------------------------------------------------------------- WHY THIS DECK
// Two short, plain lines a first-timer can read: the plan and the risk.
export function whyThisDeck(ids, byId, levelFor = () => 1) {
  const cards = ids.map(id => byId[id]).filter(Boolean);
  if (!cards.length) return { plan: 'Tap cards below to build a squad of five.', risk: '', arena: null, cover: 0 };
  const lead = cards[0];
  const arena = arenaFor(lead.type);
  const types = [...new Set(cards.map(c => c.type))];
  const hits = new Set(types.map(t => BEATS[t]));
  const threat = {};
  for (const c of cards) threat[WEAK_TO[c.type]] = (threat[WEAK_TO[c.type]] || 0) + 1;
  const worst = Object.entries(threat).sort((a, b) => b[1] - a[1])[0];
  const swift = cards.filter(c => (c.keywords || []).includes('SWIFT')).length;
  const top = cards.slice().sort((a, b) => effectivePwr(b, levelFor(b.id)) - effectivePwr(a, levelFor(a.id)))[0];
  const planBits = [];
  const tc = (x) => String(x).toLowerCase().replace(/(^|\s)([a-z])/g, (m, a, b) => a + b.toUpperCase());
  planBits.push(`${lead.name} leads into ${arena ? tc(arena.label) : 'the arena'}`);
  if (hits.size >= 4) planBits.push(`hits ${hits.size}/6 types for ×2`);
  else if (cards.length >= 3) planBits.push(`hits only ${hits.size}/6 types for ×2`);
  if (swift >= 2) planBits.push(`${swift} SWIFT strikers move first`);
  else if (top && top !== lead && cards.length >= 3) planBits.push(`${top.name} is your closer`);
  let risk = '';
  if (worst && worst[1] >= 2) risk = `${tc(worst[0])} beats ${worst[1]} of your fighters`;
  else if (cards.length >= 4) risk = 'No shared weakness — a steady squad';
  return { plan: planBits.join(' · '), risk, arena, cover: hits.size, worst: worst && worst[1] >= 2 ? worst[0] : null };
}

// ---------------------------------------------------------------- NOMAD WHEELS
// The collection set system (binder side). A nomad's WHEEL has six sockets, one
// per type; owning any of that nomad's cards of a type fills the socket. The
// socket's grade is the best rarity owned there. Pure function of the owned set.
export function nomadWheels(cards, owned) {
  const by = {};
  for (const c of cards) {
    if (!c.person || c.role === 'support' || c.altOf || c.forged) continue;
    const w = (by[c.person] ||= { person: c.person, sockets: {}, total: 0, owned: 0 });
    w.total++;
    if (!owned(c.id)) continue;
    w.owned++;
    const s = w.sockets[c.type];
    if (!s || rank(c) > rank(s)) w.sockets[c.type] = c;
  }
  const list = Object.values(by).map(w => {
    const filled = TYPE_ORDER.filter(t => w.sockets[t]).length;
    const grade = filled < 6 ? null
      : TYPE_ORDER.every(t => rank(w.sockets[t]) >= 3) ? 'PRISM'
        : TYPE_ORDER.every(t => rank(w.sockets[t]) >= 2) ? 'GOLD' : 'COMPLETE';
    return { ...w, filled, grade };
  });
  return list.sort((a, b) => b.filled - a.filled || b.owned - a.owned || a.person.localeCompare(b.person));
}
// v10 deck.mastery: { person: grade } for every graded wheel. Snapshotted into
// the deck at lock time; the engine applies it only to an active CREW bond.
export function deckMastery(wheels) {
  const out = {};
  for (const w of wheels || []) if (w.grade) out[w.person] = w.grade;
  return out;
}
const RANK = { common: 0, rare: 1, epic: 2, legendary: 3, mythic: 4, eternal: 5 };
function rank(c) { return RANK[c.rarity] ?? 0; }

// Collection level: one level per 12 distinct cards owned, +2 per complete
// wheel. Cosmetic only (frames, sigil colours) — see the design doc.
export function collectionLevel(uniqueOwned, wheelsComplete) {
  return 1 + Math.floor(uniqueOwned / 12) + wheelsComplete * 2;
}

// ---------------------------------------------------------------- AUTO
// The best squad the player can field under the budget: a greedy pass that
// values power plus the engine's own synergy bonus, then one swap-improvement
// sweep. Deterministic for a given collection.
export function autoSquad(pool, byId, levelFor = () => 1, keepLead = null) {
  const val = (ids) => {
    if (deckStars(ids, byId) > STAR_BUDGET) return -1e9;
    let p = 0;
    for (const id of ids) p += effectivePwr(byId[id], levelFor(id));
    const b = bondsFor(ids, byId).bonus;
    return p * (1 + (b.dmgPct || 0) / 100) + (b.energyPerRound || 0) * 14 + (b.chargeFaster || 0) * 10
      + new Set(ids.map(id => BEATS[byId[id].type])).size * 3;
  };
  const cand = pool.slice().sort((a, b) =>
    effectivePwr(b, levelFor(b.id)) / starCost(b) - effectivePwr(a, levelFor(a.id)) / starCost(a)
    || a.id.localeCompare(b.id)).slice(0, 60);
  let ids = keepLead ? [keepLead] : [];
  // Reserve what the remaining slots will REALLY cost: the cheapest cards
  // still available, not 1★ each. A collection with no commons used to let the
  // greedy spend the budget on stars and then find no legal fifth card.
  const allCost = pool.map(c => ({ id: c.id, k: starCost(c) })).sort((a, b) => a.k - b.k);
  const reserveFor = (taken, n) => {
    let r = 0, got = 0;
    for (const c of allCost) { if (got >= n) break; if (taken.includes(c.id)) continue; r += c.k; got++; }
    return got < n ? Infinity : r;
  };
  while (ids.length < FORGE_CHARS) {
    let best = null, bv = -1e9;
    const left = STAR_BUDGET - deckStars(ids, byId);
    const slotsAfter = FORGE_CHARS - ids.length - 1;
    for (const c of cand.concat(pool.filter(p => !cand.includes(p)))) {
      if (ids.includes(c.id) || starCost(c) + reserveFor([...ids, c.id], slotsAfter) > left) continue;
      const v = val([...ids, c.id]);
      if (v > bv) { bv = v; best = c; }
    }
    if (!best) break;
    ids.push(best.id);
  }
  for (let pass = 0; pass < 2; pass++) {
    for (let i = keepLead ? 1 : 0; i < ids.length; i++) {
      let cur = val(ids);
      for (const c of cand) {
        if (ids.includes(c.id)) continue;
        const t = ids.slice(); t[i] = c.id;
        const v = val(t);
        if (v > cur) { cur = v; ids = t; }
      }
    }
  }
  // lead: the card whose arena + power suits the squad best (highest PWR)
  if (!keepLead) ids.sort((a, b) => effectivePwr(byId[b], levelFor(b)) - effectivePwr(byId[a], levelFor(a)));
  return ids;
}

// ---------------------------------------------------------------- FATE
// Fill the empty slots by fate. Same heat weighting as LUCKY DRAW (cards you
// have not fielded lately are likelier). Fate ignores the star budget — that
// is the gamble. Uses a seeded PRNG from the caller; never Math.random.
// `budget` (multiplayer): the deal stays inside the star budget, keeping at
// least 1★ per slot still to fill. The server rejects an over-budget MP deck.
export function fateFill(ids, pool, history, seed, budget = null) {
  const rng = mulberry32(seed32(seed));
  const left = pool.filter(c => !ids.includes(c.id));
  const out = ids.slice();
  let spent = budget == null ? 0 : ids.reduce((n, id) => n + starCost(pool.find(c => c.id === id)), 0);
  while (out.length < FORGE_CHARS && left.length) {
    const room = budget == null ? Infinity : budget - spent - (FORGE_CHARS - out.length - 1);
    const cand = left.filter(c => starCost(c) <= room);
    if (!cand.length) break;
    const total = cand.reduce((n, c) => n + heatWeight(c.id, history), 0);
    let roll = rng() * total, at = cand.length - 1;
    for (let i = 0; i < cand.length; i++) { roll -= heatWeight(cand[i].id, history); if (roll <= 0) { at = i; break; } }
    const pick = cand[at];
    left.splice(left.indexOf(pick), 1);
    spent += starCost(pick);
    out.push(pick.id);
  }
  return out;
}
